import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, Grid, MenuItem, TextField } from "@mui/material";
import { Navigate } from "react-router-dom";

import Constants from "Helpers/Constants";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";

const selectFieldProps = {
  InputLabelProps: { shrink: true },
  sx: {
    "& .MuiOutlinedInput-root": {
      minHeight: 56,
      alignItems: "center",
    },
  },
};

function BulkImportAgriBoost() {
  const [redirect, setRedirect] = useState(false);
  const [allSubjects, setAllSubjects] = useState([]);
  const [allTopics, setAllTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [notification, setNotification] = useState({ open: false, color: "success", message: "" });
  const [form, setForm] = useState({
    default_subject_id: "",
    default_topic_id: "",
    cardsArray: "",
  });

  const showMessage = (message, color = "success", shouldRedirect = false) => {
    setNotification({ open: true, color, message });
    setTimeout(() => {
      setNotification({ open: false, color, message: "" });
      if (shouldRedirect) setRedirect(true);
    }, shouldRedirect ? 2500 : 4000);
  };

  useEffect(() => {
    const loadSubjects = async () => {
      try {
        const resp = await axios.get(Constants.getUrls.getAgriBoostMetaSubjects);
        setAllSubjects(resp?.data?.data || []);
      } catch (error) {
        showMessage("Unable to load subjects.", "error");
      }
    };

    loadSubjects();
  }, []);

  useEffect(() => {
    const loadTopics = async () => {
      if (!form.default_subject_id) {
        setAllTopics([]);
        return;
      }

      try {
        const resp = await axios.get(
          `${Constants.getUrls.getAgriBoostMetaTopics}?subject_id=${form.default_subject_id}`
        );
        setAllTopics(resp?.data?.data || []);
      } catch (error) {
        showMessage("Unable to load topics.", "error");
      }
    };

    loadTopics();
  }, [form.default_subject_id]);

  const handleChange = (name, value) => {
    if (name === "default_subject_id") {
      setForm((prev) => ({ ...prev, default_subject_id: value, default_topic_id: "" }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const resp = await axios.post(Constants.postUrls.createAgriBoostBulkImport, form);
      setResult(resp?.data?.data || null);
      showMessage(resp?.data?.message || "AgriBoost bulk import completed.", "success", true);
    } catch (error) {
      showMessage(error?.response?.data?.message || "Unable to bulk import AgriBoost.", "error");
    } finally {
      setLoading(false);
    }
  };

  if (redirect) return <Navigate to="/agriboost" />;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
          >
            <MDTypography variant="h6" color="white">
              Bulk Import AgriBoost
            </MDTypography>
          </MDBox>
          <MDBox py={3} px={2}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Default Subject (Optional)"
                    value={form.default_subject_id}
                    onChange={(e) => handleChange("default_subject_id", e.target.value)}
                    {...selectFieldProps}
                  >
                    <MenuItem value="">No Default Subject</MenuItem>
                    {allSubjects.map((item) => (
                      <MenuItem key={item._id} value={item._id}>
                        {item.subject_name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Default Topic (Optional)"
                    value={form.default_topic_id}
                    onChange={(e) => handleChange("default_topic_id", e.target.value)}
                    {...selectFieldProps}
                  >
                    <MenuItem value="">No Default Topic</MenuItem>
                    {allTopics.map((item) => (
                      <MenuItem key={item._id} value={item._id}>
                        {item.topic_name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <MDTypography variant="caption" color="text" display="block" mb={1}>
                    Paste a JSON array like your sample. Both camelCase and snake_case keys are accepted,
                    for example: questionEnglish, questionHindi, answerEnglish, answerHindi,
                    descriptionEnglish, descriptionHindi, revealTime, displayOrder, status and isActive.
                  </MDTypography>
                  <MDTypography variant="caption" color="text" display="block" mb={1}>
                    If subject_id or topic_id is not present inside each row, the subject and topic selected
                    above will be applied automatically during import.
                  </MDTypography>
                  <TextField
                    multiline
                    minRows={14}
                    fullWidth
                    label="AgriBoost JSON Array"
                    value={form.cardsArray}
                    onChange={(e) => handleChange("cardsArray", e.target.value)}
                  />
                </Grid>
              </Grid>

              <Grid align="left" mt={2}>
                <MDButton variant="contained" size="small" color="info" type="submit">
                  {loading ? "Importing..." : "Import AgriBoost"}
                </MDButton>
              </Grid>
            </form>

            {result ? (
              <MDBox mt={3}>
                <MDTypography variant="h6">Import Summary</MDTypography>
                <MDTypography variant="button" display="block">
                  Total Rows: {result?.totalRows || 0}
                </MDTypography>
                <MDTypography variant="button" display="block">
                  Imported Rows: {result?.importedRows || 0}
                </MDTypography>
                <MDTypography variant="button" display="block">
                  Invalid Rows: {result?.invalidRows || 0}
                </MDTypography>
                {(result?.errors || []).length ? (
                  <MDBox mt={2}>
                    {(result.errors || []).map((item, index) => (
                      <MDTypography key={`${item.row}-${index}`} variant="caption" color="error" display="block">
                        Row {item.row}: {item.message}
                      </MDTypography>
                    ))}
                  </MDBox>
                ) : null}
              </MDBox>
            ) : null}
          </MDBox>
          <MDSnackbar
            color={notification.color}
            icon="notifications"
            title="AgriQuiz"
            content={notification.message}
            dateTime="Just now"
            open={notification.open}
          />
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default BulkImportAgriBoost;
